import { Link } from "react-router-dom";
import { WebStory } from "../../models";
import { env, formatDate, getPublicStoryUrl } from "../../utils";
import { ReactComponent as DeleteIcon } from "../../assets/icons/trash.svg";
import { MouseEvent, useState } from "react";
import { useDeleteStoryMutation } from "../../store/base-api";

export default function StoryListItem({
  id,
  storyTitle,
  created,
  user,
  isPublic,
  coverImage,
  storyType,
  topLevelCategory,
  slug,
}: WebStory) {
  const [deleteStory] = useDeleteStoryMutation();
  const [deletingStory, setDeletingStory] = useState(false);

  const deleteStoryHandler = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (deletingStory || !isPublic) return;

    if (window.confirm("Are you sure you want to delete this story?")) {
      setDeletingStory(true);
      try {
        await deleteStory(id).unwrap();
      } catch (e: any) {
        window.alert(e);
      } finally {
        setDeletingStory(false);
      }
    }
  };

  const publicUrl = isPublic
    ? getPublicStoryUrl({ storyType, topLevelCategory, slug })
    : null;

  return (
    <Link
      to={`/stories/${id}`}
      style={
        deletingStory ? { opacity: 0.5, pointerEvents: "none" } : undefined
      }
      className="flex items-center gap-4 p-4 border-2 border-neutral-50 rounded-2xl bg-white shadow-md cursor-pointer hover:bg-neutral-50 no-underline text-inherit"
    >
      {coverImage ? (
        <img
          src={env("imageKit") + coverImage}
          className="w-16 h-16 rounded-lg object-cover shadow-sm flex-shrink-0"
          alt={`Cover for ${storyTitle || id}`}
        />
      ) : (
        <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
          <span className="text-gray-400 text-xs">No Image</span>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-xl font-semibold truncate">{storyTitle}</p>
        <p className="text-sm text-gray-600">by {user.name}</p>
        <p className="text-sm text-gray-500">
          created on {formatDate(created)}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <span
          className={`text-xs px-2 py-1 rounded-md ${
            isPublic
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {isPublic ? "Public" : "Private"}
        </span>

        {publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-sm px-3 py-1 rounded-md bg-blue-100 text-blue-800 hover:bg-blue-200 no-underline"
          >
            View public
          </a>
        )}

        <button
          disabled={deletingStory}
          onClick={deleteStoryHandler}
          className="p-2 svg-btn after:bg-error-500/50"
        >
          <DeleteIcon height={20} width={20} />
        </button>
      </div>
    </Link>
  );
}
